import { getGlobalConfig } from '@core/globalConfig'
import { isDefinedFalsy, isTruthy } from '@core/helpers/boolean'
import { logError } from '@core/logging'
import { StorageKeyEssentialEnum } from '@core/storage/types'
import {
  getCookieWithFallback,
  setCookieWithFallback,
} from '@core/tracking/funnelTracking'
import type { Consent } from '@core/tracking/types'
import { consents } from '@core/tracking/types'

export const CONSENT_COOKIE_KEY = StorageKeyEssentialEnum.fw_consent

function isValidConsent(consent: unknown): consent is Consent {
  if (!consent || !consents.includes(consent as Consent)) {
    logError(
      new Error(
        `Value ${consent} provided for content is invalid. Allowed consents: ${consents.join(
          ', ',
        )}`,
      ),
      {
        captureContext: {
          extra: {
            consent,
          },
        },
      },
    )
    return false
  }

  return true
}

/**
 * Function responsible for validating and storing of user consent.
 */
export function setConsent(consent?: unknown) {
  if (!isValidConsent(consent)) {
    return
  }
  setCookieWithFallback(CONSENT_COOKIE_KEY, consent)
}

/**
 * @returns The consent previously set by user
 */
export function getConsent(): Consent | undefined {
  return getCookieWithFallback(CONSENT_COOKIE_KEY)?.value as Consent | undefined
}

/**
 * @returns Whether tracking is fully disabled via the `tracking` global config
 * flag. When `tracking` is explicitly set to a falsy value (`false`, `'false'`,
 * `'0'`), callers must skip pixel sends. Unset (undefined) means enabled.
 */
export function isTrackingDisabled(): boolean {
  return isDefinedFalsy(getGlobalConfig().tracking)
}

/**
 * @returns Whether to skip sending the event for the given endpoint consent
 * level. When `tracking` is falsy, all pixel tracking is blocked regardless of
 * the user consent cookie. When `tracking_opt_in` is set, non-essential events
 * are blocked until the user explicitly consents. Otherwise, non-essential
 * events are blocked only if the user explicitly limited consent to `essential`.
 */
export function shouldBlockEndpointWithConsent(
  endpointConsent: Consent,
): boolean {
  if (isTrackingDisabled()) {
    return true
  }

  const consent = getConsent()
  const isOptInTracking = isTruthy(getGlobalConfig().tracking_opt_in)

  return (
    endpointConsent === 'non-essential' &&
    (consent === 'essential' || (isOptInTracking && !consent))
  )
}
